import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Edit3, Save, X } from 'lucide-react';
import { Button } from '@univdiam/ui/components/button';
import api from '@/http/api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useInfiniteGroup } from '@/sharedData/group';
import { Loader2 } from 'lucide-react';

interface Option {
  id: string;
  value: string;
}

interface Group {
  id: string;
  label: string;
  options: Option[];
  isEditing: boolean;
  isRaw?: boolean
  isUpdating?: boolean
  isSaving?: boolean
  isDeleting?: boolean
}


const postGroups = ({ label, options }: { label: string, options: string }) => {
  return api.post(`/admin/product_group`, { label, options });
};
const updateGroups = ({ label, options, id }: { label: string, options: string, id: string }) => {
  return api.put(`/admin/product_group/${id}`, { label, options });
};
const deleteGroup = (id: string) => {
  return api.delete(`/admin/product_group/${id}`);
};
function ProductGroup() {
  const queryClient = useQueryClient()
  const [groups, setGroups] = useState<Group[]>([]);

  const { data: infiniteGroupData, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteGroup()

  const productGroupData = infiniteGroupData?.pages?.flatMap(page => page.productGroups) || []


  useEffect(() => {
    if (productGroupData && productGroupData.length > 0) {
      setGroups(productGroupData.map((d: any) => ({ ...d, id: d.id.toString(), options: JSON.parse(d.options), isEditing: false })))
    }
  }, [infiniteGroupData])

  useEffect(() => {
    console.log('groups', groups)
  }, [groups])

  const { mutate } = useMutation({
    mutationFn: (values: { label: string, options: string, id?: string }) => {
      if (values?.id) {
        return updateGroups({
          id: values.id!,
          label: values.label,
          options: values.options
        })
      } else {
        return postGroups(values)
      }
    },
    onSuccess: async () => {
      toast.success('Group Updated')
      queryClient.invalidateQueries({ queryKey: ['product_group'], })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to save group')
    },
    onSettled: (_data, _error, variables) => {
      // Clear saving state after mutation completes
      // For existing groups: clear by id
      // For new (raw) groups: clear all raw groups (they'll be replaced by server data on refetch)
      setGroups(prev => prev.map(g =>
        variables.id
          ? (g.id === variables.id ? { ...g, isSaving: false } : g)
          : (g.isRaw ? { ...g, isSaving: false } : g)
      ))
    }
  })

  const { mutate: deleteMutate } = useMutation({
    mutationFn: (values: { id: string }) => {
      return deleteGroup(values.id)
    },
    onSuccess: async () => {
      toast.success('Group Deleted')
      queryClient.invalidateQueries({ queryKey: ['product_group'], })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete group')
    },
    onSettled: (_data, _error, variables) => {
      // Clear deleting state for this group after mutation completes
      setGroups(prev => prev.map(g =>
        g.id === variables.id ? { ...g, isDeleting: false } : g
      ))
    }
  })


  const addGroup = () => {
    const newGroup: Group = {
      id: Date.now().toString(),
      label: '',
      options: [],
      isEditing: true,
      isRaw: true
    };
    setGroups([...groups, newGroup]);
  };

  const updateGroupLabel = (groupId: string, newLabel: string) => {
    setGroups(groups.map(group =>
      group.id === groupId
        ? { ...group, label: newLabel }
        : group
    ));
  };

  const toggleGroupEdit = (groupId: string) => {
    setGroups(groups.map(group =>
      group.id === groupId
        ? { ...group, isEditing: !group.isEditing, isUpdating: true }
        : group
    ));
  };


  const addOption = (groupId: string) => {
    const newOption: Option = {
      id: `${groupId}-${Date.now()}`,
      value: ''
    };

    setGroups(groups.map(group =>
      group.id === groupId
        ? { ...group, options: [...group.options, newOption] }
        : group
    ));
  };

  const updateOption = (groupId: string, optionId: string, newValue: string) => {
    setGroups(groups.map(group =>
      group.id === groupId
        ? {
          ...group,
          options: group.options.map(option =>
            option.id === optionId
              ? { ...option, value: newValue }
              : option
          )
        }
        : group
    ));
  };

  const deleteOption = (groupId: string, optionId: string) => {
    setGroups(groups.map(group =>
      group.id === groupId
        ? { ...group, options: group.options.filter(option => option.id !== optionId) }
        : group
    ));
  };

  const deleteRawGroup = (groupId: string) => {
    setGroups(groups.filter(group => group.id !== groupId));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br p-3">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Group</h1>
          <Button
            onClick={addGroup}
            className="flex items-center space-x-1 px-4 py-2 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
          >
            <Plus size={20} />
            <span>Add New Group</span>
          </Button>
        </div>

        {/* Groups Container */}
        <div className="space-y-6">
          {groups.map((group) => (
            <div key={group.id} className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
              {/* Group Header */}
              <div className="bg-gradient-to-r from-black/10 to-white/70 px-6 py-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3 flex-1">
                    {group.isEditing ? (
                      <input
                        type="text"
                        value={group.label}
                        onChange={(e) => updateGroupLabel(group.id, e.target.value)}
                        className="text-xl font-semibold bg-white border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:border-transparent outline-none"
                        placeholder="Enter group label"
                        maxLength={100}
                      />
                    ) : (
                      <h2 className="text-xl font-semibold text-gray-800 break-words">{group.label}</h2>
                    )}
                    <span className="bg-blue-100 text-blcak text-sm font-sm px-2.5 py-0.5 rounded-full">
                      {group.options.length} options
                    </span>

                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      disabled={group.isSaving || group.isDeleting}
                      className="p-1 hover:bg-white hover:shadow-md rounded-lg transition-all duration-200 text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      title={group.isEditing ? "Save" : "Edit"}
                    >
                      {group.isEditing ? (
                        group.isSaving ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-600 border-t-transparent" />
                        ) : (
                          <Save size={18} onClick={() => {
                            // Client-side duplicate check (normalize: lowercase + strip spaces)
                            const normalize = (s: string) => s.trim().toLowerCase().replace(/\s+/g, '');
                            const isDuplicate = groups.some(g =>
                              g.id !== group.id &&
                              normalize(g.label) === normalize(group.label)
                            );

                            if (isDuplicate) {
                              toast.error(`A group with the name "${group.label}" already exists.`);
                              return;
                            }

                            if (!group.label.trim()) {
                              toast.error('Group label cannot be empty.');
                              return;
                            }

                            // Check for blank options
                            for (const option of group.options) {
                              if (!option.value.trim()) {
                                toast.error('Option values cannot be empty.');
                                return;
                              }
                            }

                            // Check for duplicate option values within the same group
                            const normalizeOption = (s: string) => s.trim().toLowerCase().replace(/\s+/g, '');
                            const seenValues = new Set<string>();
                            for (const option of group.options) {
                              const normalized = normalizeOption(option.value);
                              if (seenValues.has(normalized)) {
                                toast.error(`Duplicate value "${option.value.trim()}" already exists in this group.`);
                                return;
                              }
                              seenValues.add(normalized);
                            }

                            // Check that at least one option exists
                            if (group.options.length === 0) {
                              toast.error('Please add at least one option before saving.');
                              return;
                            }

                            // Set saving state for this group only
                            setGroups(prev => prev.map(g =>
                              g.id === group.id ? { ...g, isSaving: true } : g
                            ))

                            // If it's a new (raw) group, don't pass id so mutationFn uses POST
                            // Otherwise pass id so mutationFn uses PUT
                            if (group.isRaw) {
                              mutate({ label: group.label, options: JSON.stringify(group.options) })
                            } else {
                              mutate({ label: group.label, options: JSON.stringify(group.options), id: group.id })
                            }
                          }} />
                        )
                      ) : (
                        <Edit3 size={18} onClick={() => toggleGroupEdit(group.id)} />
                      )}
                    </button>
                    <button
                      disabled={group.isSaving || group.isDeleting}
                      onClick={() => {
                        const confirm = window.confirm("Are you sure you want to delete this group?");
                        if (!confirm) return;

                        // Set deleting state for this group only
                        setGroups(prev => prev.map(g =>
                          g.id === group.id ? { ...g, isDeleting: true } : g
                        ))

                        if (group?.isRaw) {
                          deleteRawGroup(group.id)
                        } else {
                          deleteMutate({ id: group.id })
                        }
                      }}
                      className="p-2 hover:bg-red-50 hover:shadow-md rounded-lg transition-all duration-200 text-gray-600 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Delete Group"
                    >
                      {group.isDeleting ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-red-600 border-t-transparent" />
                      ) : (
                        <Trash2 size={18} />
                      )}
                    </button>

                  </div>
                </div>
              </div>

              {/* Options List */}
              <div className="p-6">
                <div className="space-y-3">
                  {group.options.map((option) => (
                    <div key={option.id} className="flex items-center space-x-2 p-2 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors duration-200">
                      <input
                        type="text"
                        value={option.value}
                        onChange={(e) => updateOption(group.id, option.id, e.target.value)}
                        className="flex-1 bg-transparent border-none outline-none font-sm text-gray-700 placeholder-gray-400 break-words"
                        placeholder="Enter option value"
                        disabled={!group.isEditing}
                        maxLength={100}
                      />
                      {
                        group.isEditing && <button
                          onClick={() => deleteOption(group.id, option.id)}
                          className="p-1.5 hover:bg-red-100 rounded-md transition-colors duration-200 text-gray-500 hover:text-red-600"
                          title="Delete Option"
                        >
                          <X size={16} />
                        </button>
                      }
                    </div>
                  ))}

                  {group.options.length === 0 && (
                    <div className="text-center py-8 text-gray-400">
                      <p>No options added yet</p>
                    </div>
                  )}
                </div>

                {/* Add Option Button */}
                {
                  group.isEditing && <button
                    onClick={() => addOption(group.id)}
                    className="mt-4 w-full flex items-center justify-center space-x-2 py-3 px-4 border-2 border-dashed border-gray-300 rounded-lghover:bg-blue-50 transition-all duration-200 text-gray-600 cursor-pointer"
                  >
                    <Plus size={20} />
                    <span>Add Option</span>
                  </button>
                }

              </div>
            </div>
          ))}

          {/* Load More Button */}
          {hasNextPage && fetchNextPage && (
            <div className="flex justify-center mt-6">
              <Button 
                variant="outline" 
                onClick={() => fetchNextPage()} 
                disabled={isFetchingNextPage}
                className="w-full max-w-[200px]"
              >
                {isFetchingNextPage ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  'Load More Groups'
                )}
              </Button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

export default ProductGroup;